import React, { useContext, useEffect, useState, memo } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import * as FileSystem from 'expo-file-system/legacy'
import FileViewer from 'react-native-file-viewer'
import styled, { useTheme } from 'styled-components/native'
import { useActionSheet } from '@expo/react-native-action-sheet'
import Mattermost from 'APP/Services/Mattermost'
import I18n from 'APP/I18n'
import { showToast } from 'APP/Converse/Toast/helpers'
import Analytics from 'APP/Services/Analytics'
import { ChatContext, PostContext } from 'APP/Lib/Context'
import Card from 'APP/Components/Chat/Card'
import { isWeb, isIOS } from 'APP/Helpers/checkPlatform'
import FileViewerModal from 'APP/Components/Chat/FileViewerModal'
import { logger } from 'APP/Lib/Datadog'
import Icon from 'APP/Converse/Icon'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { FileInfo } from '@mattermost/types/files'

const ImageContainer = styled.TouchableOpacity`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.practitionerMsgBg};
`

const ImagePreviewContainer = styled.Image<{ dataSet?: { ddPrivacy: string } }>`
  border-radius: 10px;
  width: 240px;
  height: 300px;
`

const ActivityIndicatorContainer = styled.ActivityIndicator`
  border-radius: 10px;
  width: 240px;
  height: 300px;
`

const FailedIconContainer = styled.View`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 240px;
  height: 300px;
`

const CardContainer = styled.View<{ alignRight?: boolean }>`
  align-items: ${({ alignRight }) => (alignRight ? 'flex-end' : 'flex-start')};
`

const FilesContainer = styled.View`
  flex-direction: column;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
  width: fit-content;
  max-width: 240px;
`

const validFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'pdf']
const validImageTypes = ['jpg', 'jpeg', 'png', 'gif', 'heic']

type FileData = {
  fileId: string
  metadata?: FileInfo
  url?: string
}

const File = () => {
  const theme = useTheme()
  const postContext = useContext(PostContext)
  const post = postContext?.post
  const isMember = postContext?.isMember
  const chatContext = useContext(ChatContext)
  const channelId = chatContext?.channelId

  const dispatch = useDispatch()
  const { showActionSheetWithOptions } = useActionSheet()

  const { accessToken } = useSelector((state) => state.login)
  const [files, setFiles] = useState<FileData[]>([])
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null)
  const fileIds = post?.file_ids
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null)

  const postFailed = post?.props?.failed

  useEffect(() => {
    if (post && fileIds && fileIds.length > 0 && post.id) {
      const mattermost = Mattermost.create(accessToken)

      // Get all file metadata for the post once
      Promise.all([
        mattermost.getPostFileInfo({ postId: post.id }),
        // Get public URLs for all fileIds
        ...fileIds.map((fileId) => mattermost.getPublicUrl({ fileId })),
      ])
        .then(([metadataResponse, ...urls]) => {
          const allMetadata = metadataResponse
          const filesData = fileIds.map((fileId, index) => {
            // Find the metadata that matches this fileId
            const metadata = allMetadata.find((file) => file.id === fileId)
            return {
              fileId,
              metadata,
              url: urls[index],
            }
          })

          setFiles(filesData)
        })
        .catch((error) => {
          logger.error('Failed to load file metadata', {}, error)
          setFiles([])
        })
    }
  }, [post, accessToken, fileIds])

  const handleCloseModal = () => {
    setModalFileUrl(null)
  }

  const handleLongPress = () => {
    const postId = post?.id
    if (!channelId || !postId) return

    const options = [I18n.t('ConversationScreen.actionSheets.cancel')]

    showActionSheetWithOptions(
      {
        options,
      },
      () => {
        Analytics.trackEvent('button_click', { button_value: 'Cancel send file' })
        // @ts-expect-error FIXME: removePendingPost is only valid for pending posts, but we have not narrowed the type here
        dispatch(PatientHistoryActions.removePendingPost(channelId, postId))
      }
    )
  }

  const handleLongPressError = () => {
    const postId = post?.id
    if (!channelId || !postId) return

    const options = [
      I18n.t('ConversationScreen.actionSheets.retry'),
      I18n.t('ConversationScreen.actionSheets.cancel'),
    ]

    showActionSheetWithOptions(
      {
        options,
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          Analytics.trackEvent('button_click', { button_value: 'Retry send file' })
          // @ts-expect-error FIXME: retryPostRequest is only valid for pending posts, but we have not narrowed the type here
          dispatch(PatientHistoryActions.retryPostRequest(channelId, postId))
        } else {
          Analytics.trackEvent('button_click', { button_value: 'Cancel send file' })
          // @ts-expect-error FIXME: removePendingPost is only valid for pending posts, but we have not narrowed the type here
          dispatch(PatientHistoryActions.removePendingPost(channelId, postId))
        }
      }
    )
  }

  const viewFileWeb = (fileData: FileData) => {
    const { metadata, url } = fileData
    if (!url) return

    if (metadata?.extension === 'pdf') {
      window.open(url)
    } else {
      setModalFileUrl(url)
    }
  }

  const viewFileMobile = async (fileData: FileData) => {
    const { metadata, url, fileId } = fileData
    if (!url || !metadata) return

    setLoadingFileId(fileId)
    const documentsDirectoryPath = FileSystem.documentDirectory + 'Documents/'
    const localFilePath = documentsDirectoryPath + metadata.name
    const options = {
      headers: { cookie: `MMAUTHTOKEN=${accessToken}` },
      // Use FOREGROUND session to avoid NSURLErrorCancelled (-999) in the embedded/brownfield
      // context where the host app's background URL session management interferes with the
      // default BACKGROUND session type used by expo-file-system on iOS.
      sessionType:
        isEmbeddedEnvironment() && isIOS()
          ? FileSystem.FileSystemSessionType.FOREGROUND
          : undefined,
    }

    const handleError = (_error?: unknown) => {
      setLoadingFileId(null)
      showToast({
        title: I18n.t('error.alert.title'),
        description: I18n.t('ConversationScreen.fileDownloadError'),
        variant: 'error',
      })
    }

    try {
      const dirInfo = await FileSystem.getInfoAsync(documentsDirectoryPath)
      if (!dirInfo.exists) {
        await FileSystem.makeDirectoryAsync(documentsDirectoryPath, { intermediates: true })
      }
      const file = await FileSystem.downloadAsync(url, localFilePath, options)
      FileViewer.open(file.uri).catch(handleError)
    } catch (err) {
      handleError(err)
    } finally {
      setLoadingFileId(null)
    }
  }

  const viewFile = async (fileData: FileData) => {
    try {
      Analytics.trackEvent('button_click', { button_value: 'Attachment tap' })

      if (isWeb()) {
        viewFileWeb(fileData)
      } else {
        await viewFileMobile(fileData)
      }
    } catch (err) {
      logger.error('Failed to view file', {}, err)
    }
  }

  if (!files || files.length === 0) {
    return (
      <ImageContainer
        testID="LONG_PRESS_VIEW"
        onLongPress={postFailed ? handleLongPressError : handleLongPress}
      >
        {postFailed ? (
          <FailedIconContainer testID="RETRY_STATE">
            <Icon src="reload" variant="material" color="text" />
          </FailedIconContainer>
        ) : (
          <ActivityIndicatorContainer
            testID="LOADING_STATE"
            size="large"
            color={theme.colors.text}
          />
        )}
      </ImageContainer>
    )
  }

  const renderFile = (fileData: FileData) => {
    const { metadata, url, fileId } = fileData
    const isFileLoading = loadingFileId === fileId
    const extension = metadata?.extension
    const isImage =
      (extension != null && validImageTypes.includes(extension)) ||
      (url != null && /\.(jpg|jpeg|png|gif|heic)$/i.test(url))

    // Image preview
    if (isImage && url) {
      return (
        <ImageContainer testID="IMAGE" onPress={() => viewFile(fileData)}>
          {isFileLoading ? (
            <ActivityIndicatorContainer size="large" color={theme.colors.text} />
          ) : (
            <ImagePreviewContainer
              source={{ uri: url }}
              resizeMode="cover"
              dataSet={{ ddPrivacy: 'hidden' }}
            />
          )}
        </ImageContainer>
      )
    }

    // PDF file preview
    if (metadata && metadata.extension === 'pdf') {
      return (
        <CardContainer alignRight={isMember}>
          <Card
            testID="PDF_FILE"
            title={metadata.name}
            subtitle={I18n.t('ConversationScreen.file', {
              extension: metadata.extension.toLocaleUpperCase(),
            })}
            onPress={() => viewFile(fileData)}
            iconName="download"
            isLoading={isFileLoading}
          />
        </CardContainer>
      )
    }

    // Invalid file type
    if (metadata && !validFileTypes.includes(metadata.extension)) {
      return (
        <Card
          testID="INVALID_FILE"
          title={I18n.t('ConversationScreen.restricted.main')}
          subtitle={I18n.t('ConversationScreen.restricted.subText')}
          iconName="information"
        />
      )
    }
  }

  return (
    <>
      <FilesContainer>
        {files.map((fileData, index) => (
          <React.Fragment key={`file-${fileData.fileId || index}`}>
            {renderFile(fileData)}
          </React.Fragment>
        ))}
      </FilesContainer>
      {isWeb() && modalFileUrl && (
        <FileViewerModal
          src={modalFileUrl}
          isOpened={!!modalFileUrl}
          handleCloseModal={handleCloseModal}
        />
      )}
    </>
  )
}

export default memo(File)
